import React, { useState } from "react";
import { Row, Col, Typography, Card, Button, notification, message } from "antd";
import { LockOutlined } from "@ant-design/icons";
import planoContratualService from "services/PlanoContratualService";
import cupomService from "services/CupomService";
import CheckoutPagamento from "components/organism/CheckoutPagamento";
import ComposicaoValorFlip from "views/app-views/assinatura/components/ComposicaoValorFlip";

const { Title, Paragraph } = Typography;

/**
 * Flip-escolha (#2) — tela de ESCOLHA exibida na conta-bloqueada quando o motor
 * v2 bloqueou por falta-de-escolha (passou do limite de alunos, prazo do flip
 * venceu). Reusa a mecânica dos cards + checkout do banner de /assinatura
 * (index.js:1348-1577): passo 1 escolhe mensal/anual, passo 2 paga.
 *
 * Roteamento idêntico ao banner ativo: anual (cross_ciclo) -> trocarPlanoV2;
 * mensal -> confirmarFlipAgora. No sucesso (DONE/PAID) NÃO navega manualmente —
 * chama onPago() (o aguardarDesbloqueio do pai polla /dados-conta-bloqueada até
 * bloqueado===false e redireciona). Isso fecha a invariante #1 no front: pagou
 * -> back vira ATIVA na mesma transação -> poll detecta -> sai da tela.
 *
 * Nota de dívida técnica (não-bloqueante): o ideal é extrair o IIFE de
 * cards+checkout de /assinatura/index.js pra um componente compartilhado e usar
 * nas duas telas. Pra manter o diff mínimo nesta entrega, é uma cópia adaptada;
 * as duas podem divergir — a extração é um follow-up.
 */
const FlipEscolhaBloqueada = ({
	planoAtual,
	userData,
	taxasParcelamentoCartao,
	onPago,
	// PROATIVO (opcional): quando vem do aviso pré-bloqueio (FlipPendenteModal), é
	// o nº de dias que faltam até o bloqueio — muda só o cabeçalho ("faltam N dias"
	// em vez de "o prazo terminou"). Ausente/0 = contexto BLOQUEADO (conta-bloqueada).
	diasRestantes,
}) => {
	const opcoes = planoAtual?.flip_opcoes || [];
	const temEscolha = opcoes.length > 1;
	const proativo = typeof diasRestantes === "number" && diasRestantes > 0;

	const [opcaoFlipEscolhida, setOpcaoFlipEscolhida] = useState(null);
	const [checkoutError, setCheckoutError] = useState("");
	const [checkoutResult, setCheckoutResult] = useState(null);
	// eslint-disable-next-line no-unused-vars
	const [pixCobrancaId, setPixCobrancaId] = useState(null);
	const [loading, setLoading] = useState(false);

	// Estado de cupom local (replicado de /assinatura) — mantém a paridade do
	// anual com cupom: a opção ANUAL (cross_ciclo) abate o desconto do fixo.
	const [cupomAtivo, setCupomAtivo] = useState(null);
	const [aplicandoCupom, setAplicandoCupom] = useState(false);

	const handleAplicarCupom = async (codigoArg) => {
		const codigo = (codigoArg || "").trim();
		if (!codigo) {
			message.warning("Digite o código do cupom.");
			return;
		}
		setAplicandoCupom(true);
		try {
			await cupomService.resgatarCupom({
				codigo,
				origem: "checkout_logado",
			});
			const novo = await cupomService.obterMeuCupom().catch(() => null);
			setCupomAtivo(novo || null);
			message.success(
				"Cupom aplicado! O desconto já está no valor do plano.",
			);
		} catch (err) {
			const msg =
				err?.response?.data?.message || "Cupom inválido ou expirado.";
			message.error(msg);
		} finally {
			setAplicandoCupom(false);
		}
	};

	const handleRemoverCupom = () => {
		setCupomAtivo(null);
	};

	// Roteamento (adaptado de handleConfirmarFlipSubmit:466-525). Opção efetiva =
	// a escolhida, ou a única quando não há escolha.
	// - FRONTEIRA da fase (flip_escolha_prepaga): QUALQUER opção vai pela troca
	//   PREPAGA — paga o fixo cheio agora; em fase o plano novo começa na
	//   virada, bloqueado aplica na hora e reativa.
	// - Flip normal: anual (cross_ciclo) = troca-ciclo (resíduo abatido);
	//   mensal = flip da diferença (pró-rata).
	// No sucesso chama onPago().
	const handleSubmit = async (payload) => {
		try {
			setLoading(true);
			setCheckoutError("");

			const opcaoEfetiva =
				opcaoFlipEscolhida ||
				(opcoes.length === 1 ? opcoes[0] : null);

			const resp = planoAtual?.flip_escolha_prepaga
				? await planoContratualService.trocarPlanoPrepagoV2({
						...payload,
						idPlanoDestino: opcaoEfetiva?.id,
				  })
				: opcaoEfetiva?.cross_ciclo
				? await planoContratualService.trocarPlanoV2({
						...payload,
						idPlanoDestino: opcaoEfetiva.id,
				  })
				: await planoContratualService.confirmarFlipAgora(payload);

			if (resp?.status === "DONE" || resp?.status === "PAID") {
				notification.success({
					message: "Plano ajustado",
					description:
						planoAtual?.flip_escolha_prepaga && proativo
							? "Pagamento confirmado. Seu plano novo começa no fim do período promocional — sem cobrança extra até lá."
							: "Estamos liberando seu acesso. Aguarde alguns instantes...",
				});
				setCheckoutResult(null);
				onPago(); // dispara aguardarDesbloqueio do pai -> poll -> redirect
				return;
			}

			setCheckoutResult(resp);
			if (resp?.status === "PIX" && resp?.cobranca?.id) {
				setPixCobrancaId(resp.cobranca.id);
				// PIX: mostra o QR e inicia o poll em paralelo. O poll só
				// redireciona quando bloqueado virar false (webhook confirmou).
				onPago();
			}
		} catch (error) {
			const descricaoErro =
				error.response?.data?.message ||
				error.response?.data?.errors?.[0]?.description ||
				error.response?.data?.error?.message ||
				error.message;
			setCheckoutError(
				descricaoErro ||
					"Não foi possível processar o pagamento. Verifique os dados e tente novamente.",
			);
		} finally {
			setLoading(false);
		}
	};

	const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

	const opcaoAtiva =
		opcaoFlipEscolhida || (opcoes.length === 1 ? opcoes[0] : null);
	// FRONTEIRA da fase (flip + fase juntos, flag do back): a escolha é PREPAGA
	// — paga o fixo CHEIO do destino agora; em fase o plano novo começa na
	// virada, bloqueado aplica na hora e reativa.
	const prepaga = !!planoAtual?.flip_escolha_prepaga;

	return (
		<>
			{/* Cabeçalho da escolha (substitui o "pendência financeira") */}
			<Card
				style={{
					textAlign: "center",
					marginBottom: "24px",
					borderColor: "#faad14",
					backgroundColor: "#fffbe6",
				}}
			>
				<LockOutlined
					style={{
						fontSize: "48px",
						color: "#faad14",
						marginBottom: "16px",
					}}
				/>
				<Title level={3} style={{ marginBottom: "8px" }}>
					{prepaga && proativo
						? "Seu período promocional está acabando"
						: proativo
						? "Você passou do limite de alunos"
						: "Escolha seu plano para continuar"}
				</Title>
				<Paragraph
					style={{
						fontSize: "16px",
						color: "#595959",
						marginBottom: "0",
					}}
				>
					{prepaga && proativo ? (
						<>
							Seu período promocional termina em{" "}
							<strong>
								{diasRestantes}{" "}
								{diasRestantes === 1 ? "dia" : "dias"}
							</strong>{" "}
							e você está com mais alunos do que o plano de
							entrada comporta.{" "}
							{temEscolha
								? "Escolha entre o plano mensal ou anual e pague agora"
								: `Pague o ${
										opcoes[0]?.nome || "novo plano"
								  } agora`}{" "}
							— o plano novo começa na virada, sem cobrança extra
							até lá.
						</>
					) : prepaga ? (
						<>
							Seu período promocional terminou e você está com
							mais alunos do que o plano de entrada comporta.{" "}
							{temEscolha
								? "Escolha entre o plano mensal ou anual e finalize o pagamento"
								: `Finalize o pagamento do ${
										opcoes[0]?.nome || "novo plano"
								  }`}{" "}
							para reativar o acesso.
						</>
					) : proativo ? (
						<>
							Faltam{" "}
							<strong>
								{diasRestantes}{" "}
								{diasRestantes === 1 ? "dia" : "dias"}
							</strong>{" "}
							para sua conta ser limitada.{" "}
							{temEscolha
								? "Escolha entre o plano mensal ou anual e finalize o pagamento"
								: `Finalize o pagamento do ${
										opcoes[0]?.nome || "novo plano"
								  }`}{" "}
							para manter todos os seus alunos ativos.
						</>
					) : (
						<>
							Você passou do limite de alunos do plano atual e o
							prazo para ajustar terminou.{" "}
							{temEscolha
								? "Escolha entre o plano mensal ou anual e finalize o pagamento"
								: `Finalize o pagamento do ${
										opcoes[0]?.nome || "novo plano"
								  }`}{" "}
							para reativar o acesso.
						</>
					)}
				</Paragraph>
			</Card>

			<Card>
				{/* Passo 1 — escolher (quando há +de 1 opção e nada escolhido) */}
				{temEscolha && !opcaoAtiva ? (
					<>
						<div style={{ marginBottom: 16 }}>
							<strong>Você passou do limite de alunos.</strong>{" "}
							Escolha o seu novo plano:
						</div>
						<Row gutter={[16, 16]}>
							{opcoes.map((op) => {
								const cm = op.frequencia_pagamento_meses || 1;
								const porMes = op.cross_ciclo
									? op.valor_fixo / cm
									: op.valor_fixo;
								return (
									<Col xs={24} sm={12} key={op.id}>
										<div
											onClick={() =>
												setOpcaoFlipEscolhida(op)
											}
											style={{
												border: `1px solid ${
													op.cross_ciclo
														? "#ff7a18"
														: "#e8e8e8"
												}`,
												borderRadius: 12,
												padding: 16,
												cursor: "pointer",
												height: "100%",
												boxShadow: op.cross_ciclo
													? "0 4px 16px rgba(255,122,24,.12)"
													: "none",
											}}
										>
											<div
												style={{
													fontWeight: 700,
													fontSize: 15,
												}}
											>
												{op.nome}
											</div>
											<div
												style={{
													fontSize: 22,
													fontWeight: 800,
													marginTop: 4,
												}}
											>
												{op.cross_ciclo
													? fmt(op.valor_fixo)
													: fmt(porMes)}
												<span
													style={{
														fontSize: 13,
														fontWeight: 400,
														color: "rgba(0,0,0,.45)",
													}}
												>
													{" "}
													{op.cross_ciclo
														? op.frequencia_pagamento_meses ===
														  12
															? "por ano"
															: "por semestre"
														: "/mês"}
												</span>
											</div>
											{op.cross_ciclo && (
												<div
													style={{
														fontSize: 12,
														color: "rgba(0,0,0,.45)",
													}}
												>
													equivalente a {fmt(porMes)}
													/mês
												</div>
											)}
											<div
												style={{
													fontSize: 13,
													color: "#fa541c",
													fontWeight: 600,
													marginTop: 6,
												}}
											>
												+ {fmt(op.valor_variavel_aluno)}{" "}
												por aluno ativo/mês
											</div>
											<Button
												type="primary"
												block
												style={{
													marginTop: 12,
													...(op.cross_ciclo
														? {
																background:
																	"#ff7a18",
																borderColor:
																	"#ff7a18",
														  }
														: {}),
												}}
											>
												Escolher {op.nome}
											</Button>
										</div>
									</Col>
								);
							})}
						</Row>
						<div
							style={{
								marginTop: 14,
								fontSize: 12.5,
								color: "rgba(0,0,0,.45)",
							}}
						>
							{prepaga && proativo
								? "Você paga o plano novo agora e ele começa no fim do período promocional — sem cobrança extra até lá."
								: prepaga
								? "Você paga o valor cheio do plano novo e seu acesso é reativado na hora."
								: "Você paga agora; o que sobrou do mês atual é abatido. Só reativa quando o pagamento confirmar."}
						</div>
					</>
				) : (
					// Passo 2 — checkout da opção escolhida (ou destino único)
					(() => {
						const valorAPagar = opcaoAtiva
							? (opcaoAtiva.valor_a_pagar_centavos || 0) / 100
							: (planoAtual?.flip_prorata_centavos || 0) / 100;
						const nomeDestino =
							opcaoAtiva?.nome ||
							planoAtual?.flip_plano_destino?.nome ||
							"o plano por aluno";
						return (
							<>
								{temEscolha && (
									<Button
										type="link"
										style={{
											paddingLeft: 0,
											marginBottom: 4,
										}}
										onClick={() =>
											setOpcaoFlipEscolhida(null)
										}
									>
										← escolher outro plano
									</Button>
								)}
								<div
									style={{
										marginBottom: 16,
										color: "rgba(0,0,0,.65)",
									}}
								>
									{prepaga && proativo ? (
										<>
											Você paga o{" "}
											<strong>{nomeDestino}</strong> agora
											e ele começa no fim do período
											promocional. Até lá seu plano atual
											continua valendo — sem cobrança
											extra.
										</>
									) : prepaga ? (
										<>
											Ao confirmar, seu plano passa para{" "}
											<strong>{nomeDestino}</strong> e seu
											acesso é reativado na hora. Você
											paga o valor cheio do novo plano.
										</>
									) : (
										<>
											Ao confirmar, seu plano passa para{" "}
											<strong>{nomeDestino}</strong> e
											você paga agora{" "}
											{opcaoAtiva?.cross_ciclo
												? "o valor do novo plano, com o resíduo do mês atual abatido"
												: "apenas a diferença proporcional pelo restante do ciclo"}
											.
										</>
									)}
								</div>
								<ComposicaoValorFlip
									prepaga={prepaga}
									crossCiclo={!!opcaoAtiva?.cross_ciclo}
									fixoAtual={
										(planoAtual?.preco_fixo_centavos ??
											0) / 100
									}
									fixoNovo={Number(
										opcaoAtiva?.valor_fixo ??
											planoAtual?.flip_plano_destino
												?.valor_fixo ??
											0,
									)}
									valorAPagar={valorAPagar}
									pagoAte={planoAtual?.pago_ate}
								/>
								<CheckoutPagamento
									cobrancaUnica
									planName={nomeDestino}
									features={[]}
									// Cupom na opção ANUAL do flip (cross_ciclo) e em
									// QUALQUER opção da troca PREPAGA (o preparar
									// desconta o fixo). No flip mensal (pró-rata) não
									// entra cupom.
									permiteCupom={
										!!opcaoAtiva?.cross_ciclo || prepaga
									}
									cupomAtivo={cupomAtivo}
									planoParaCupom={opcaoAtiva}
									onAplicarCupom={handleAplicarCupom}
									onRemoverCupom={handleRemoverCupom}
									aplicandoCupom={aplicandoCupom}
									fixedValue={valorAPagar}
									variableMonthlyValue={0}
									studentsCount={0}
									totalValue={valorAPagar}
									maxInstallments={
										opcaoAtiva?.cross_ciclo
											? opcaoAtiva.frequencia_pagamento_meses ||
											  1
											: 1
									}
									taxasParcelamentoCartao={
										taxasParcelamentoCartao
									}
									loading={loading}
									errorMessage={checkoutError}
									result={checkoutResult}
									cartaoSalvo={userData?.dados_cartao_plano || null}
									onSubmit={handleSubmit}
									submitLabel="Confirmar e pagar"
									initialHolderEmail={userData?.email || ""}
									initialHolderCpfCnpj={userData?.cpf || ""}
									initialHolderPostalCode={userData?.cep || ""}
									initialHolderAddressNumber={
										userData?.numero_endereco || ""
									}
									initialHolderAddressComplement={
										userData?.complemento_endereco || ""
									}
									initialHolderPhone={
										userData?.telefone ||
										userData?.telefone_celular ||
										""
									}
									initialHolderMobilePhone={
										userData?.telefone_celular || ""
									}
								/>
							</>
						);
					})()
				)}
			</Card>
		</>
	);
};

export default FlipEscolhaBloqueada;
